import React, {useEffect, useState} from 'react';
import post from '../utils/post.js';
import AppHeader from '../components/AppHeader.js';
import Search from '../components/Search.js';
import EditableProfilePhoto from '../components/EditableProfilePhoto.js';
import InstructorUserNew from '../components/InstructorUserNew.js';
import InstructorUserEdit from '../components/InstructorUserEdit.js';
import clipboard from '../utils/clipboard.js';
import '../styles/instructorUser.scss';

function getDisplayName(user) {
	if ((user.NICKNAME || '') !== '') {
		return `${user.LASTNAME} ${user.FIRSTNAME} (${user.NICKNAME})`;
	}

	return `${user.LASTNAME} ${user.FIRSTNAME}`.trim();
}

function getMetricsLabel(user) {
	const age = parseInt(user.AGE, 10) || 0;
	const weight = parseInt(user.WEIGHT, 10) || 0;
	const parts = [];

	if (age > 0) {
		parts.push(String(age));
	}

	if (weight > 0) {
		parts.push(`${weight}kg`);
	}

	return parts.join(', ');
}

function getTargetLabel(user) {
	return String(user.TARGET || '').trim();
}

function getDaysLabel(days) {
	if (days === 1) {
		return 'den';
	}

	if (days >= 2 && days <= 4) {
		return 'dny';
	}

	return 'dnů';
}

function getLastLessonLabel(user) {
	const lessonDate = String(user.LAST_LESSON_DT || '').trim();
	const lessonType = String(user.LAST_LESSON_TYPE || '').trim();
	const lessonDays = parseInt(user.LAST_LESSON_DAYS, 10);

	if (lessonDate === '' || lessonType === '' || Number.isNaN(lessonDays) || lessonDays < 0) {
		return '';
	}

	if (lessonDays === 0) {
		return `${lessonDate} (Dnes), ${lessonType}`;
	}

	if (lessonDays === 1) {
		return `${lessonDate} (Včera), ${lessonType}`;
	}

	return `${lessonDate} (${lessonDays} ${getDaysLabel(lessonDays)}), ${lessonType}`;
}

export default function InstructorUser({usrInfo, timerCnt}) {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [searchValue, setSearchValue] = useState('');
	const [, setOffset] = useState(0);
	const [loadedCnt, setLoadedCnt] = useState(0);
	const [info, setInfo] = useState('');
	const [showNew, setShowNew] = useState(false);
	const [showEdit, setShowEdit] = useState(false);
	const [dataEdit, setDataEdit] = useState(null);

	useEffect(() => {
		let search = '';

		if (typeof window.location.pathname.split('/')[2] !== 'undefined') {
			search = decodeURI(window.location.pathname.split('/')[2]);
			setSearchValue(search);
		}

		getData(search, 0);

		if (search !== '') {
			window.history.pushState({}, document.title, '/instructorusr');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getData = (search, targetOffset) => {
		setLoading(true);

		post('getListInstructorUser', {search, offset: targetOffset})
			.then((res) => {
				if (targetOffset === 0) {
					setData(res.data.data);
				} else {
					setData((prevRows) => [...prevRows, ...res.data.data]);
				}

				setLoadedCnt(res.data.data.length);
				setInfo(`Moji klienti: ${res.data.data_cnt}`);
				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const onChangeSearch = (value) => {
		setSearchValue(value);
		setOffset(0);
		getData(value, 0);
	};

	const loadNext = () => {
		setOffset((prevOffset) => {
			const nextOffset = prevOffset + usrInfo.LIST_ROWS;
			getData(searchValue, nextOffset);
			return nextOffset;
		});
	};

	const handleNew = () => {
		setShowNew(true);
	};

	const closeNew = () => {
		setShowNew(false);
	};

	const refreshList = () => {
		setOffset(0);
		getData(searchValue, 0);
	};

	const handleEdit = (user) => {
		setDataEdit(user);
		setShowEdit(true);
	};

	const handleMusclegroup = (user) => {
		window.location.pathname = `/musclegroup/${user.ID}`;
	};

	const handleWorkout = (user) => {
		window.location.pathname = `/workout/${user.ID}`;
	};

	const handleExercise = (user) => {
		window.location.pathname = `/exercise/${user.ID}`;
	};

	const closeEdit = () => {
		setShowEdit(false);
	};

	return (
		<>
			<div className="page_main instructor-user-page-main">
				<AppHeader usrInfo={usrInfo} timerCnt={timerCnt} loading={loading} />
				<Search showSearch searchValue={searchValue} onChangeSearch={(value) => onChangeSearch(value)} showNew handleNew={handleNew}>
					<span className="instructor-user-page__summary">{info}</span>
				</Search>
				<InstructorUserNew showNew={showNew} closeNew={closeNew} refreshList={refreshList} />
				<InstructorUserEdit showEdit={showEdit} closeEdit={closeEdit} refreshList={refreshList} data={dataEdit} />

				<div className={`page instructor-user-page${usrInfo.sysAdmin === 1 || usrInfo.manager === 1 || usrInfo.instructor === 1 ? ' more' : ''}`}>
					<div className="instructor-user-page__list">
						{data.length > 0 ? data.map((user, index) => {
							const displayName = getDisplayName(user);
							const isMember = parseInt(user.CLUBMEMBER, 10) === 1;
							const hasPermanentMember = parseInt(user.PERNAMENTMEMBER, 10) === 1;
							const isNotRegistered = parseInt(user.REGISTERED, 10) === 0;
							const metricsLabel = getMetricsLabel(user);
							const targetLabel = getTargetLabel(user);
							const lastLessonLabel = getLastLessonLabel(user);
							return (
								<article className="user-page__card instructor-user-page__card" key={user.PAIR_ID || user.ID || index}>
									<div className="user-page__card-head instructor-user-page__card-head">
										<div className="user-page__identity">
											<div className="user-page__avatar-wrap instructor-user-page__avatar-wrap">
												<EditableProfilePhoto
													photoUrl={user.PROFILE_PHOTO_URL}
													displayName={displayName}
													canEditPhoto={true}
													uploadUrl="/rest/uploadInstructorProfilePhoto"
													deleteUrl="/rest/deleteInstructorProfilePhoto"
													entityKey={user.ID}
													avatarWidth="6rem"
													avatarHeight="6rem"
												/>
											</div>

											<div className="user-page__profile instructor-user-page__profile">
												<button type="button" className="user-page__name" onClick={() => clipboard(displayName)}>
													{displayName}
												</button>
												{user.PHONES !== '' ? <a className="user-page__link" href={`tel:${user.PHONES}`}>{user.PHONES}</a> : <span className="user-page__link instructor-user-page__muted">Bez telefonu</span>}
												{user.EMAIL !== '' ? <a className="user-page__link user-page__link--email" href={`mailto:${user.EMAIL}`}>{user.EMAIL}</a> : null}
												{lastLessonLabel !== '' ? <div className="instructor-user-page__last-lesson fnt_green">{lastLessonLabel}</div> : null}
											</div>
										</div>

										<div className="user-page__head-side instructor-user-page__head-side">
											<div className="user-page__head-top instructor-user-page__head-top">
												<div className="user-page__flag-row instructor-user-page__flag-row">
													{isNotRegistered ? <span className="user-page__flag instructor-user-page__member-chip instructor-user-page__member-chip--registered">NENÍ REG</span> : null}
													{hasPermanentMember ? <span className="user-page__flag instructor-user-page__member-chip instructor-user-page__member-chip--permanent">PERM</span> : null}
													<span className={`user-page__flag instructor-user-page__member-chip${isMember ? ' is-valid' : ''}`}>
														{isMember ? 'ČLEN' : 'NENÍ ČLEN'}
													</span>
												</div>
												<div className="user-page__id-chip instructor-user-page__id-chip">{user.ID}</div>
											</div>
											{metricsLabel !== '' ? (
												<div className="instructor-user-page__meta-row">
													<span className="instructor-user-page__metrics-chip">{metricsLabel}</span>
												</div>
											) : null}
											<div className="user-page__action-grid instructor-user-page__action-grid">
												<button type="button" className="user-page__icon-button" onClick={() => handleExercise(user)} aria-label={`Výkony uživatele ${displayName}`}>
													<img className="icon2 exercise" alt="" />
												</button>
												<button type="button" className="user-page__icon-button" onClick={() => handleWorkout(user)} aria-label={`Cviky uživatele ${displayName}`}>
													<img className="icon2 workout" alt="" />
												</button>
												<button type="button" className="user-page__icon-button" onClick={() => handleMusclegroup(user)} aria-label={`Svalové partie uživatele ${displayName}`}>
													<img className="icon2 musclegroup" alt="" />
												</button>
												<button type="button" className="user-page__icon-button" onClick={() => handleEdit(user)}>
													<img className="icon2 edit" alt="" />
												</button>
											</div>
										</div>
									</div>
									{targetLabel !== '' ? <div className="instructor-user-page__target fnt_orange">Cíl: {targetLabel}</div> : null}
								</article>
							);
						}) : <div className="instructor-user-page__empty">Žádní přiřazení uživatelé nebyli nalezeni.</div>}
					</div>

					<div className="instructor-user-page__load-more">
						{parseInt(loadedCnt, 10) === parseInt(usrInfo.LIST_ROWS, 10) ? (
							<button className="button small loadNext instructor-user-page__button" onClick={() => loadNext()}>
								Načíst další
							</button>
						) : (
							<span className="fnt_12 loadNext instructor-user-page__load-all">Byly načteny všechny záznamy</span>
						)}
					</div>
				</div>
			</div>
		</>
	);
}